/**
 * ContentRuleCenterConnected
 * A wrapper component that connects ContentRuleCenter to the Jotai store
 * using the useContentRuleCenter hook
 */
import React, { FC } from 'react';
import ContentRuleCenter from './index';
import { useContentRuleCenter } from '../../../hooks/useContentRuleCenter';

export interface ContentRuleCenterConnectedProps {
  /**
   * Sort configuration
   */
  sortConfig?: {
    field: 'severity' | 'lastUpdated';
    direction: 'asc' | 'desc';
  };
  /**
   * Callback when sort changes
   */
  onSortChange?: (field: 'severity' | 'lastUpdated') => void;
}

/**
 * ContentRuleCenterConnected - Connected version of ContentRuleCenter
 * Automatically fetches data and manages state using Jotai store
 */
const ContentRuleCenterConnected: FC<ContentRuleCenterConnectedProps> = ({
  sortConfig,
  onSortChange,
}) => {
  const {
    // Loading States
    isLoading,
    isTableLoading,
    error,

    // Data
    allRulesCount,
    mustComplyCount,
    shouldComplyCount,
    goodToHaveCount,
    categories,
    countries,
    brands,
    audiences,
    channels,
    categoryFilters,
    selectedFilters,

    // Handlers
    handleFiltersBatchChange,
    handleRetry,
    handleAccordionOpen,
    handleLoadMoreRules,
  } = useContentRuleCenter();

  return (
    <ContentRuleCenter
      allRulesCount={allRulesCount}
      mustComplyCount={mustComplyCount}
      shouldComplyCount={shouldComplyCount}
      goodToHaveCount={goodToHaveCount}
      categories={categories}
      countries={countries}
      brands={brands}
      audiences={audiences}
      channels={channels}
      categoryFilters={categoryFilters}
      selectedFilters={selectedFilters}
      onFiltersChange={filters => {
        // Batch update all filters at once - triggers only ONE API call
        handleFiltersBatchChange({
          countries: filters.countries,
          brands: filters.brands,
          audiences: filters.audiences,
          channels: filters.channels,
          categories: filters.categories,
        });
      }}
      isLoading={isLoading}
      isTableLoading={isTableLoading}
      error={error}
      onRetry={handleRetry}
      onAccordionOpen={handleAccordionOpen}
      onLoadMoreRules={handleLoadMoreRules}
      sortConfig={sortConfig}
      onSortChange={onSortChange}
    />
  );
};

ContentRuleCenterConnected.displayName = 'ContentRuleCenterConnected';

export default ContentRuleCenterConnected;
